/**
 * Home quick actions (Oct 2026, Falco's feedback): one row of chips under the
 * greeting for the things people do most, without opening a tool first.
 *
 *   - Clock in / Start break / End break / Clock out — TODAY only, for people
 *     who track their own time (live on today's entry, same callable as /time).
 *   - Report sick leave / Request time off — open the Absence request form
 *     pre-set to the type (`/absence?request=<type>`).
 *
 * Renders nothing when the user has neither tool.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { CalendarOff, LogIn, LogOut, Pause, Play, Thermometer } from 'lucide-react';
import {
  COLLECTIONS,
  TIME_ENTRY_ID,
  minutesToHhMm,
  resolveLeaveTypes,
  isSickLeaveType,
  isOvertimeCompensationType,
  type AppConfig,
  type TimeEntry,
  type User as UserDoc,
} from '@daaily/shared';
import { db } from '../lib/firebase';
import { Icon } from '../design/Icon';
import { callTimeClock, clockState, todayStr, workedMinutesLive } from '../timeTracking/lib';

const chip = 'inline-flex items-center gap-2 h-9 px-3.5 rounded-md border border-line bg-surface text-sm text-fg hover:bg-surface-2 hover:border-line-strong transition-colors duration-fast disabled:opacity-50';

export function QuickActions({ user, config }: { user: UserDoc; config: AppConfig }) {
  const tracks = !!user.tools?.timeTracking?.enabled && user.timeTracksOwn !== false && !!user.weeklyWorkingHours && !!user.timeTrackingStartDate;
  const absence = !!user.tools?.absence?.enabled;
  const leaveTypes = resolveLeaveTypes(config);
  const sickType = leaveTypes.find((t) => isSickLeaveType(t.name))?.name ?? null;
  const timeOffType = leaveTypes.find((t) => t.name === 'Vacation')?.name
    ?? leaveTypes.find((t) => !isSickLeaveType(t.name) && !isOvertimeCompensationType(leaveTypes, t.name))?.name
    ?? null;

  // Today's entry, live — the same doc the header clock watches (Firestore
  // shares the listener), so Home and the top bar never disagree.
  const today = todayStr();
  const [entry, setEntry] = useState<TimeEntry | undefined>(undefined);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const email = user.email.toLowerCase();
  useEffect(() => {
    if (!tracks) return;
    const ref = doc(db, COLLECTIONS.TIME_ENTRIES, TIME_ENTRY_ID(email, today));
    return onSnapshot(ref, (snap) => setEntry(snap.exists() ? (snap.data() as TimeEntry) : undefined));
  }, [tracks, email, today]);
  const cs = clockState(entry);
  useEffect(() => {
    if (!cs.checkedIn) return;
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, [cs.checkedIn]);

  if (!tracks && !absence) return null;

  const clock = (action: 'check_in' | 'start_break' | 'end_break' | 'check_out') => {
    setError(null);
    setBusy(true);
    callTimeClock({ date: today, action, tzOffsetMinutes: new Date().getTimezoneOffset() })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Action failed.'))
      .finally(() => setBusy(false));
  };
  const worked = workedMinutesLive(entry, now);

  return (
    <section aria-label="Quick actions" className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {tracks && !cs.checkedIn && (
          <button type="button" onClick={() => clock('check_in')} disabled={busy} className={chip}>
            <Icon as={LogIn} size={16} className="text-success" />
            {cs.checkedOut ? 'Clock in again' : 'Clock in'}
            {cs.checkedOut && <span className="text-xs text-fg-faint">· {minutesToHhMm(worked)} today</span>}
          </button>
        )}
        {tracks && cs.checkedIn && (
          <>
            <button type="button" onClick={() => clock(cs.onBreak ? 'end_break' : 'start_break')} disabled={busy} className={chip}>
              <Icon as={cs.onBreak ? Play : Pause} size={16} className="text-info" />
              {cs.onBreak ? 'End break' : 'Start break'}
            </button>
            {!cs.onBreak && (
              <button type="button" onClick={() => clock('check_out')} disabled={busy} className={chip}>
                <Icon as={LogOut} size={16} className="text-fg-muted" />
                Clock out
                <span className="text-xs text-fg-faint tabular-nums">· {minutesToHhMm(worked)}</span>
              </button>
            )}
          </>
        )}
        {absence && sickType && (
          <Link to={`/absence?request=${encodeURIComponent(sickType)}`} className={chip}>
            <Icon as={Thermometer} size={16} className="text-danger" />
            Report sick leave
          </Link>
        )}
        {absence && timeOffType && (
          <Link to={`/absence?request=${encodeURIComponent(timeOffType)}`} className={chip}>
            <Icon as={CalendarOff} size={16} className="text-success" />
            Request time off
          </Link>
        )}
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </section>
  );
}
